import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Plus, Edit, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Deals = () => {
  const { t } = useTranslation();
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_info: '',
    status: 'new',
    priority: 'medium',
    value: 0,
    assigned_to: '',
    tagged_users: [],
    meetings_count: 0,
    details: ''
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchDeals();
    fetchUsers();
  }, []);

  const fetchDeals = async () => {
    try {
      const res = await axios.get(`${API}/deals`);
      setDeals(res.data);
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const toggleTaggedUser = (userId) => {
    const updated = formData.tagged_users.includes(userId)
      ? formData.tagged_users.filter(id => id !== userId)
      : [...formData.tagged_users, userId];
    setFormData({...formData, tagged_users: updated});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/deals/${editingId}`, formData);
        toast.success('Сделката бе актуализирана!');
      } else {
        await axios.post(`${API}/deals`, formData);
        toast.success('Сделката бе добавена!');
      }
      fetchDeals();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Сигурни ли сте?')) {
      try {
        await axios.delete(`${API}/deals/${id}`);
        toast.success('Сделката бе изтрита!');
        fetchDeals();
      } catch (error) {
        toast.error('Грешка при изтриване');
      }
    }
  };

  const handleEdit = (deal) => {
    setFormData({
      ...deal,
      tagged_users: deal.tagged_users || [],
      meetings_count: deal.meetings_count || 0
    });
    setEditingId(deal.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      client_name: '',
      client_info: '',
      status: 'new',
      priority: 'medium',
      value: 0,
      assigned_to: '',
      tagged_users: [],
      meetings_count: 0,
      details: ''
    });
    setEditingId(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'badge-blue',
      in_progress: 'badge-orange',
      won: 'badge-green',
      lost: 'badge-red'
    };
    return colors[status] || 'badge-blue';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'priority-high',
      medium: 'priority-medium',
      low: 'priority-low'
    };
    return colors[priority] || 'priority-medium';
  };

  const statusLabels = {
    new: 'Нова',
    in_progress: 'В процес',
    won: 'Спечелена',
    lost: 'Загубена'
  };

  const priorityLabels = {
    high: 'Висок',
    medium: 'Среден',
    low: 'Нисък'
  };

  return (
    <div className="page-container" data-testid="deals-page">
      <div className="page-header">
        <h1 className="page-title">Сделки</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-deal-btn">
              <Plus size={20} /> {t('add')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? t('edit') : t('add')} Сделка</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="deal-form" data-testid="deal-form">
              <Input
                placeholder="Име на клиент (компания/физическо лице)"
                value={formData.client_name}
                onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                required
              />
              <Input
                placeholder="Информация за клиента (брой автомобили, дейност и т.н.)"
                value={formData.client_info}
                onChange={(e) => setFormData({...formData, client_info: e.target.value})}
              />
              <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">Нова</SelectItem>
                  <SelectItem value="in_progress">В процес</SelectItem>
                  <SelectItem value="won">Спечелена</SelectItem>
                  <SelectItem value="lost">Загубена</SelectItem>
                </SelectContent>
              </Select>
              <Select value={formData.priority} onValueChange={(val) => setFormData({...formData, priority: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Висок приоритет</SelectItem>
                  <SelectItem value="medium">Среден приоритет</SelectItem>
                  <SelectItem value="low">Нисък приоритет</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Стойност (лв)"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: parseFloat(e.target.value)})}
                required
              />
              <select
                value={formData.assigned_to}
                onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                className="form-select"
              >
                <option value="">Назначи към служител</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
              
              <Input
                type="number"
                placeholder="Брой проведени срещи"
                value={formData.meetings_count}
                onChange={(e) => setFormData({...formData, meetings_count: parseInt(e.target.value) || 0})}
              />

              {/* Tagged Users Multi-Select */}
              <div className="form-field">
                <label className="field-label">Тагнати потребители (ще получат нотификации)</label>
                <div className="permissions-grid">
                  {users.map(user => (
                    <label key={user.id} className="permission-item">
                      <input
                        type="checkbox"
                        checked={formData.tagged_users.includes(user.id)}
                        onChange={() => toggleTaggedUser(user.id)}
                      />
                      <span>{user.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <textarea
                placeholder="Детайли (допълнителна информация за сделката, очаквания, срокове)"
                value={formData.details}
                onChange={(e) => setFormData({...formData, details: e.target.value})}
                className="form-textarea"
              />
              <div className="form-actions">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  {t('cancel')}
                </Button>
                <Button type="submit" data-testid="save-deal-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="deals-grid">
        {deals.map(deal => {
          const assignedUser = users.find(u => u.id === deal.assigned_to);
          return (
            <div key={deal.id} className="deal-card" data-testid={`deal-card-${deal.id}`}>
              <div className="deal-card-header">
                <div>
                  <h3>{deal.client_name}</h3>
                  <p className="deal-info">{deal.client_info}</p>
                </div>
                <div className="deal-badges">
                  <span className={`status-badge ${getStatusColor(deal.status)}`}>
                    {statusLabels[deal.status]}
                  </span>
                  <span className={`priority-badge ${getPriorityColor(deal.priority)}`}>
                    {priorityLabels[deal.priority]}
                  </span>
                </div>
              </div>

              <div className="deal-card-body">
                <div className="deal-info-row">
                  <span>Стойност:</span>
                  <span className="deal-value">{deal.value} лв</span>
                </div>
                {assignedUser && (
                  <div className="deal-info-row">
                    <span>Назначен към:</span>
                    <span>{assignedUser.name}</span>
                  </div>
                )}
                {deal.meetings_count > 0 && (
                  <div className="deal-info-row">
                    <span>Проведени срещи:</span>
                    <span>{deal.meetings_count}</span>
                  </div>
                )}
                {deal.tagged_users && deal.tagged_users.length > 0 && (
                  <div className="deal-info-row">
                    <span>Тагнати:</span>
                    <span>{deal.tagged_users.length} потребител(и)</span>
                  </div>
                )}
              </div>

              <div className="deal-card-footer">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedDeal(expandedDeal === deal.id ? null : deal.id)}
                  data-testid={`expand-deal-${deal.id}`}
                >
                  {expandedDeal === deal.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  Детайли
                </Button>
                <div className="card-actions">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(deal)} data-testid={`edit-deal-${deal.id}`}>
                    <Edit size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(deal.id)} data-testid={`delete-deal-${deal.id}`}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>

              {expandedDeal === deal.id && deal.details && (
                <div className="deal-details" data-testid={`details-${deal.id}`}>
                  <p>{deal.details}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Deals;
